import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors, FlexBox } from '@brg/ui';
import { useModal } from './modal.module';

export const useZoom = () => {
    const modal = useModal();
    const show = (img: string, minZoom?: number, maxZoom?: number): void =>
        modal.show('zoom', { img, minZoom, maxZoom });
    const hide = (): void => modal.hide();
    return { show, hide };
};

export interface CoreZoomModalContentProps {
    img?: string;
    maxZoom?: number;
    minZoom?: number;
}

const CoreZoomScalePanel = styled(FlexBox)`
    position: absolute;
    bottom: 40px;
    align-items: center;
    z-index: 200;
    background: ${Colors.galBlack100};
    padding: 8px;
    border-radius: 16px;

    left: 50%;
    transform: translate(-50%, 0);
`;

const CoreZoomPanelButton = styled(FlexBox)`
    color: ${Colors.geoWhite47};
    padding: 8px;
    cursor: pointer;
`;

const CoreZoomPanelRange = styled.input`
    -webkit-appearance: none;
    width: 184px;
    height: 1px;
    margin: 0 0 0 0;
    outline: none;
    background: rgba(254, 254, 254, 0.31);

    ::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 23px;
        height: 23px;
        background: #fefefe;
        border-radius: 50%;
        cursor: pointer;
        outline: none;
        transition: 0.2s;
    }
`;

export const CoreZoomModalContentWrapper = styled.div`
    position: absolute;
    z-index: 10;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;

const CoreZoomMask = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 150;
    overflow-y: auto;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const CoreZoomer = styled.div<{ minZoom: number; maxZoom: number }>`
    display: flex;
    width: 100%;
    height: ${(p) => 100 * (p.maxZoom + 1) - p.minZoom * 100}vh;
`;

interface CoreZoomControlPanelProps {
    value: number;
    onChange: (value: number) => void;
    minZoom: number;
    maxZoom: number;

    onPlus: () => void;
    onMinus: () => void;
}

export const CoreZoomControlPanel: FC<CoreZoomControlPanelProps> = (props) => {
    const onRangeChange = (ev: any) => (ev ? props.onChange(ev.target.value) : null);

    return (
        <CoreZoomScalePanel>
            <CoreZoomPanelButton onClick={props.onMinus}>-</CoreZoomPanelButton>
            <CoreZoomPanelRange
                value={props.value}
                onChange={onRangeChange}
                min={props.minZoom}
                max={props.maxZoom}
                step={0.01}
                type={'range'}
            />
            <CoreZoomPanelButton onClick={props.onPlus}>+</CoreZoomPanelButton>
        </CoreZoomScalePanel>
    );
};

export const CoreZoomModalContent: FC<CoreZoomModalContentProps> = (props) => {
    const { img } = props;
    const maxZoom = props.maxZoom ?? 4;
    const minZoom = props.minZoom ?? 0.9;

    const [zoomLevel, setZoomLevel] = useState(minZoom);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [doublePosition, setDoublePosition] = useState({ x: 0, y: 0 });
    const [scrollTop, setScrollTop] = useState(0);
    const imgRef = useRef<any>();
    const zoomerRef = useRef<any>();

    const [startMove, setStartMove] = useState({ x: 0, y: 0 });
    const [startMoving, setStartMoving] = useState(false);

    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (imgRef.current) {
            const target = imgRef.current as HTMLImageElement;
            const parent = target.parentElement as HTMLDivElement;

            const conHeight = parent.offsetHeight;
            const conWidth = parent.offsetWidth;

            let w, h: number;

            if (target.offsetWidth > target.offsetHeight) {
                const k = conWidth / target.offsetWidth;
                w = conWidth;
                h = target.offsetHeight * k;
            } else {
                const k = conHeight / target.offsetHeight;
                h = conHeight;
                w = target.offsetWidth * k;
            }

            setImageSize({ width: w, height: h });
        }
    }, [imgRef, imageLoaded]);

    const onZoomPanelChange = (value: number): void => {
        if (value > maxZoom || value < minZoom) return;

        setZoomLevel(value);
        if (zoomerRef.current) {
            const zoomerTarget = zoomerRef.current as HTMLImageElement;
            zoomerTarget.scrollTop = zoomerTarget.offsetHeight * (value - 1);
        }
    };

    const onScrollEvent = (ev: React.UIEvent) => {
        const target = ev.target as HTMLDivElement;
        const screenHeight = target.offsetHeight;
        const scrollTop = target.scrollTop;
        const nextZoomLevel = scrollTop / screenHeight + minZoom;
        setScrollTop(scrollTop);
        setPosition((prevState) => {
            if (nextZoomLevel <= minZoom) {
                return { x: 0, y: 0 };
            }

            const dx = doublePosition.x * scrollTop;
            const dy = doublePosition.y * scrollTop;

            if (nextZoomLevel > minZoom && nextZoomLevel < zoomLevel && nextZoomLevel < maxZoom) {
                return { x: dx, y: dy };
            }

            return { x: prevState.x, y: prevState.y };
        });
        setZoomLevel(nextZoomLevel);
    };

    const onMouseDown = (ev: React.MouseEvent): void => {
        setStartMove({ x: ev.pageX - position.x, y: ev.pageY - position.y });
        setStartMoving(true);
    };

    const onMouseUp = (): void => {
        setStartMoving(false);
        setDoublePosition({ x: scrollTop ? position.x / scrollTop : 0, y: scrollTop ? position.y / scrollTop : 0 });
    };

    const onMouseMove = (ev: React.MouseEvent): void => {
        if (startMoving) {
            const target = ev.target as HTMLDivElement;

            setPosition((prevState) => {
                const imageRealWidth = imageSize.width * zoomLevel;
                const imageRealHeight = imageSize.height * zoomLevel;

                // Доступная высота для движения (высота контейнера)
                const availHeight = target.parentElement?.offsetHeight ?? window.screen.availHeight;

                // Условия начала перетягивания
                const allowMoveX = imageRealWidth > target.offsetWidth;
                const allowMoveY = imageRealHeight > availHeight;

                let dX = allowMoveX ? startMove.x - ev.pageX : prevState.x;
                let dY = allowMoveY ? startMove.y - ev.pageY : prevState.y;

                const realDX = dX * zoomLevel;
                const realDY = dY * zoomLevel;

                const overflowX = imageRealWidth - target.offsetWidth;
                const overflowY = imageRealHeight - availHeight;

                if (overflowX >= 0 && (realDX <= -(overflowX / 2) || realDX >= overflowX / 2)) dX = -prevState.x;
                if (overflowY >= 0 && (realDY <= -(overflowY / 2) || realDY >= overflowY / 2)) dY = -prevState.y;

                return { x: -dX, y: -dY };
            });
        }
    };

    return (
        <CoreZoomModalContentWrapper>
            <CoreZoomControlPanel
                onPlus={() => onZoomPanelChange(zoomLevel + 0.01)}
                onMinus={() => onZoomPanelChange(zoomLevel - 0.01)}
                minZoom={minZoom}
                maxZoom={maxZoom}
                value={zoomLevel}
                onChange={onZoomPanelChange}
            />
            <CoreZoomMask
                onContextMenu={(ev) => ev.preventDefault()}
                ref={zoomerRef}
                onScroll={onScrollEvent}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
            >
                <CoreZoomer minZoom={minZoom} maxZoom={maxZoom} />
            </CoreZoomMask>
            <FlexBox flex={'1 1'} height={'100vh'} width={'100%'} align={'center'} justify={'center'}>
                <img
                    alt=""
                    onContextMenu={(ev) => ev.preventDefault()}
                    ref={imgRef}
                    src={img}
                    style={{
                        display: 'block',
                        height: imageSize.height > imageSize.width ? '100%' : '',
                        width: imageSize.width > imageSize.height ? '100%' : '',
                        transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                    }}
                    onLoad={() => setImageLoaded(true)}
                />
            </FlexBox>
        </CoreZoomModalContentWrapper>
    );
};
import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors, FlexBox } from '@brg/ui';
import { useModal } from './modal.module';

export const useZoom = () => {
    const modal = useModal();
    const show = (img: string, minZoom?: number, maxZoom?: number): void =>
        modal.show('zoom', { img, minZoom, maxZoom });
    const hide = (): void => modal.hide();
    return { show, hide };
};

export interface CoreZoomModalContentProps {
    img?: string;
    maxZoom?: number;
    minZoom?: number;
}

const CoreZoomScalePanel = styled(FlexBox)`
    position: absolute;
    bottom: 40px;
    align-items: center;
    z-index: 200;
    background: ${Colors.galBlack100};
    padding: 8px;
    border-radius: 16px;

    left: 50%;
    transform: translate(-50%, 0);
`;

const CoreZoomPanelButton = styled(FlexBox)`
    color: ${Colors.geoWhite47};
    padding: 8px;
    cursor: pointer;
`;

const CoreZoomPanelRange = styled.input`
    -webkit-appearance: none;
    width: 184px;
    height: 1px;
    margin: 0 0 0 0;
    outline: none;
    background: rgba(254, 254, 254, 0.31);

    ::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 23px;
        height: 23px;
        background: #fefefe;
        border-radius: 50%;
        cursor: pointer;
        outline: none;
        transition: 0.2s;
    }
`;

export const CoreZoomModalContentWrapper = styled.div`
    position: absolute;
    z-index: 10;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;

const CoreZoomMask = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 150;
    overflow-y: auto;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const CoreZoomer = styled.div<{ minZoom: number; maxZoom: number }>`
    display: flex;
    width: 100%;
    height: ${(p) => 100 * (p.maxZoom + 1) - p.minZoom * 100}vh;
`;

interface CoreZoomControlPanelProps {
    value: number;
    onChange: (value: number) => void;
    minZoom: number;
    maxZoom: number;

    onPlus: () => void;
    onMinus: () => void;
}

export const CoreZoomControlPanel: FC<CoreZoomControlPanelProps> = (props) => {
    const onRangeChange = (ev: any) => (ev ? props.onChange(ev.target.value) : null);

    return (
        <CoreZoomScalePanel>
            <CoreZoomPanelButton onClick={props.onMinus}>-</CoreZoomPanelButton>
            <CoreZoomPanelRange
                value={props.value}
                onChange={onRangeChange}
                min={props.minZoom}
                max={props.maxZoom}
                step={0.01}
                type={'range'}
            />
            <CoreZoomPanelButton onClick={props.onPlus}>+</CoreZoomPanelButton>
        </CoreZoomScalePanel>
    );
};

export const CoreZoomModalContent: FC<CoreZoomModalContentProps> = (props) => {
    const { img } = props;
    const maxZoom = props.maxZoom ?? 4;
    const minZoom = props.minZoom ?? 0.9;

    const [zoomLevel, setZoomLevel] = useState(minZoom);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [doublePosition, setDoublePosition] = useState({ x: 0, y: 0 });
    const [scrollTop, setScrollTop] = useState(0);
    const imgRef = useRef<any>();
    const zoomerRef = useRef<any>();

    const [startMove, setStartMove] = useState({ x: 0, y: 0 });
    const [startMoving, setStartMoving] = useState(false);

    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (imgRef.current) {
            const target = imgRef.current as HTMLImageElement;
            const parent = target.parentElement as HTMLDivElement;

            const conHeight = parent.offsetHeight;
            const conWidth = parent.offsetWidth;

            let w, h: number;

            if (target.offsetWidth > target.offsetHeight) {
                const k = conWidth / target.offsetWidth;
                w = conWidth;
                h = target.offsetHeight * k;
            } else {
                const k = conHeight / target.offsetHeight;
                h = conHeight;
                w = target.offsetWidth * k;
            }

            setImageSize({ width: w, height: h });
        }
    }, [imgRef, imageLoaded]);

    const onZoomPanelChange = (value: number): void => {
        if (value > maxZoom || value < minZoom) return;

        setZoomLevel(value);
        if (zoomerRef.current) {
            const zoomerTarget = zoomerRef.current as HTMLImageElement;
            zoomerTarget.scrollTop = zoomerTarget.offsetHeight * (value - 1);
        }
    };

    const onScrollEvent = (ev: React.UIEvent) => {
        const target = ev.target as HTMLDivElement;
        const screenHeight = target.offsetHeight;
        const scrollTop = target.scrollTop;
        const nextZoomLevel = scrollTop / screenHeight + minZoom;
        setScrollTop(scrollTop);
        setPosition((prevState) => {
            if (nextZoomLevel <= minZoom) {
                return { x: 0, y: 0 };
            }

            const dx = doublePosition.x * scrollTop;
            const dy = doublePosition.y * scrollTop;

            if (nextZoomLevel > minZoom && nextZoomLevel < zoomLevel && nextZoomLevel < maxZoom) {
                return { x: dx, y: dy };
            }

            return { x: prevState.x, y: prevState.y };
        });
        setZoomLevel(nextZoomLevel);
    };

    const onMouseDown = (ev: React.MouseEvent): void => {
        setStartMove({ x: ev.pageX - position.x, y: ev.pageY - position.y });
        setStartMoving(true);
    };

    const onMouseUp = (): void => {
        setStartMoving(false);
        setDoublePosition({ x: scrollTop ? position.x / scrollTop : 0, y: scrollTop ? position.y / scrollTop : 0 });
    };

    const onMouseMove = (ev: React.MouseEvent): void => {
        if (startMoving) {
            const target = ev.target as HTMLDivElement;

            setPosition((prevState) => {
                const imageRealWidth = imageSize.width * zoomLevel;
                const imageRealHeight = imageSize.height * zoomLevel;

                // Доступная высота для движения (высота контейнера)
                const availHeight = target.parentElement?.offsetHeight ?? window.screen.availHeight;

                // Условия начала перетягивания
                const allowMoveX = imageRealWidth > target.offsetWidth;
                const allowMoveY = imageRealHeight > availHeight;

                let dX = allowMoveX ? startMove.x - ev.pageX : prevState.x;
                let dY = allowMoveY ? startMove.y - ev.pageY : prevState.y;

                const realDX = dX * zoomLevel;
                const realDY = dY * zoomLevel;

                const overflowX = imageRealWidth - target.offsetWidth;
                const overflowY = imageRealHeight - availHeight;

                if (overflowX >= 0 && (realDX <= -(overflowX / 2) || realDX >= overflowX / 2)) dX = -prevState.x;
                if (overflowY >= 0 && (realDY <= -(overflowY / 2) || realDY >= overflowY / 2)) dY = -prevState.y;

                return { x: -dX, y: -dY };
            });
        }
    };

    return (
        <CoreZoomModalContentWrapper>
            <CoreZoomControlPanel
                onPlus={() => onZoomPanelChange(zoomLevel + 0.01)}
                onMinus={() => onZoomPanelChange(zoomLevel - 0.01)}
                minZoom={minZoom}
                maxZoom={maxZoom}
                value={zoomLevel}
                onChange={onZoomPanelChange}
            />
            <CoreZoomMask
                onContextMenu={(ev) => ev.preventDefault()}
                ref={zoomerRef}
                onScroll={onScrollEvent}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
            >
                <CoreZoomer minZoom={minZoom} maxZoom={maxZoom} />
            </CoreZoomMask>
            <FlexBox flex={'1 1'} height={'100vh'} width={'100%'} align={'center'} justify={'center'}>
                <img
                    alt=""
                    onContextMenu={(ev) => ev.preventDefault()}
                    ref={imgRef}
                    src={img}
                    style={{
                        display: 'block',
                        height: imageSize.height > imageSize.width ? '100%' : '',
                        width: imageSize.width > imageSize.height ? '100%' : '',
                        transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                    }}
                    onLoad={() => setImageLoaded(true)}
                />
            </FlexBox>
        </CoreZoomModalContentWrapper>
    );
};
